import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import {
  updateEmail,
  sendEmailVerification,
  EmailAuthProvider,
  reauthenticateWithCredential,
  RecaptchaVerifier,
  multiFactor,
  PhoneMultiFactorGenerator,
  MultiFactorInfo,
  User,
  MultiFactorSession,
  PhoneAuthProvider,
  MultiFactorResolver,
  PhoneInfoOptions,
  MultiFactorAssertion,
  getMultiFactorResolver,
  MultiFactorError,
} from "firebase/auth";
import { useAuth } from "../../context/AuthContext";

export interface AccountEditProps {
  close: () => void;
}

const EditEmail = ({ close }: AccountEditProps) => {
  const [email, setEmail] = useState("");
  const [verifyEmail, setVerifyEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [recaptchaVerifier, setRecaptchaVerifier] = useState<RecaptchaVerifier | null>(null);
  const [isValidForm, setIsValidForm] = useState(false);
  const [showVerificationCodeInput, setShowVerificationCodeInput] = useState(false);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});
  const [MFError, setMFError] = useState<MultiFactorError | null>(null);

  // const [multiFactorSession, setMultiFactorSession] = useState<MultiFactorSession | null>(null);

  // const [phoneFactorUid, setPhoneFactorUid] = useState<string | null>(null);

  const authContext = useAuth();
  const auth = authContext?.auth;

  useEffect(() => {
    // Set up reCAPTCHA verifier for phone authentication
    if (!auth) return;
    const verifier = new RecaptchaVerifier(auth, "recaptcha-container", { size: "invisible" });
    verifier.render();
    setRecaptchaVerifier(verifier);
  }, [auth]);

  const schema = Yup.object({
    email: Yup.string().email().required("Email is required"),
    verifyEmail: Yup.string()
      .email()
      .required("Confirm Email is required")
      .oneOf([Yup.ref("email")], "Email must match"),
    password: Yup.string().required("Password is required"),
  });

  const schema2 = Yup.object().shape({
    otp: Yup.string().required("Verification code required").max(6, "OTP not valid").min(6, "OTP not valid"),
  });

  // useEffect(() => {
  //   const updateMultifactor = async (user: User) => {
  //     if (user) {
  //       const mfaUsr = await multiFactor(user);
  //       if (mfaUsr.enrolledFactors) {
  //         // Get the user's multi-factor info
  //         const factors = mfaUsr.enrolledFactors as MultiFactorInfo[];
  //         const phoneFactor = factors.find(factor => factor.factorId === "phone"); // Find the phone factor

  //         if (phoneFactor) {
  //           setPhoneFactorUid(phoneFactor.uid);
  //         }
  //       }
  //     }
  //   };
  //   const user = auth?.currentUser;
  //   user && updateMultifactor(user);
  // }, []);

  // useEffect(() => {
  //   if (!auth) return;
  //   if (!captchaVerifier) {
  //     const verifier = new RecaptchaVerifier(auth, "recaptcha-container", {
  //       size: "invisible",
  //     });
  //     verifier.render().then(id => {
  //       setWidgetId(id); // Save widget ID to reset reCAPTCHA later
  //     });
  //     setCaptchaVerifier(verifier);
  //   }
  // }, [captchaVerifier, auth]);

  useEffect(() => {
    const validate = async (email: string, verifyEmail: string, password: string) => {
      try {
        await schema.validate({ email, verifyEmail, password }, { abortEarly: true });
        setIsValidForm(true);
      } catch (err) {
        setIsValidForm(false);
      }
    };
    validate(email, verifyEmail, password);
  }, [email, verifyEmail, password]);

  useEffect(() => {
    const validate = async (otp: string) => {
      try {
        await schema2.validate({ otp }, { abortEarly: true });
        setIsValidForm(true);
      } catch (err) {
        setIsValidForm(false);
      }
    };
    validate(otp);
  }, [otp]);

  const onInputEvent = async (event: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (event.type === "focus") {
      setErrors(prev => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
      return;
    }
    try {
      if (name === "otp") {
        await schema2.validateAt(name, { [name]: value });
      } else {
        if (name === "verifyEmail") {
          await schema.validateAt(name, { email, [name]: value });
        } else {
          await schema.validateAt(name, { [name]: value });
        }
      }

      setErrors(prev => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
      return true;
    } catch (err) {
      let error = err as Yup.ValidationError;
      setErrors(prev => ({ ...prev, [name]: error.message }));
    }
  };

  // Function to verify the entered OTP
  const updateEmailAddress = async () => {
    if (!auth || !auth.currentUser?.email) return;
    try {
      const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updateEmail(auth.currentUser, email);
    } catch (error: any) {
      if (error.code === "auth/multi-factor-auth-required" && recaptchaVerifier) {
        console.error("Multi-factor authentication required:", error);

        const resolver: MultiFactorResolver = getMultiFactorResolver(auth, error);

        if (!resolver) {
          console.error("Multi-factor resolver is not available");
          return;
        }

        // Assume the user has already added an MFA method, like a phone number
        // const multiFactorInfo = resolver.hints[0]; // Getting the first multi-factor hint (typically phone number)

        // Retrieve the multi-factor resolver from the error

        setMFError(error);

        if (!auth || !auth.currentUser?.email || !recaptchaVerifier) return;

        if (auth.currentUser) {
          const mfaUsr = await multiFactor(auth.currentUser);
          if (mfaUsr.enrolledFactors) {
            // Get the user's multi-factor info
            const factors = mfaUsr.enrolledFactors as MultiFactorInfo[];
            const phoneFactor = factors.find(factor => factor.factorId === "phone"); // Find the phone factor

            const phoneAuthProvider = new PhoneAuthProvider(auth);
            const verificationId = await phoneAuthProvider.verifyPhoneNumber(
              {
                multiFactorHint: phoneFactor,
                session: resolver.session, // MFA session
              },
              recaptchaVerifier
            );

            setVerificationId(verificationId);
          }
        }

        // Ask the user to enter the verification code sent to their phone
      } else {
        console.error("Error verifying code:", error);
      }
    }
  };

  const updateEmailWithMF = async () => {
    if (!auth || !auth.currentUser?.email || !MFError || !verificationId) return;
    const resolver: MultiFactorResolver = getMultiFactorResolver(auth, MFError);

    if (!resolver) {
      console.error("Multi-factor resolver is not available");
      return;
    }

    if (otp) {
      // Use PhoneAuthProvider to create the phone credential
      const phoneCredential = PhoneAuthProvider.credential(verificationId, otp);

      // Generate the multi-factor assertion for the phone credential
      const multiFactorAssertion: MultiFactorAssertion = PhoneMultiFactorGenerator.assertion(phoneCredential);

      // Complete the multi-factor authentication
      await resolver.resolveSignIn(multiFactorAssertion);

      // Retry updating the email after MFA is successful
      await updateEmail(auth.currentUser, email);
      close();
      alert("Email updated successfully!");
    } else {
      console.error("Verification code not provided.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-50">
      {MFError ? (
        <div className="flex flex-col gap-3 p-6 bg-white rounded-lg shadow-lg w-80">
          <h2 className="mb-4 text-lg font-semibold">Verification Code</h2>
          <input
            onFocus={onInputEvent}
            onBlur={onInputEvent}
            className="verificationcodetext"
            type="text"
            name="otp"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            value={otp}
            onChange={e => setOtp(e.target.value)}
            placeholder="Enter OTP"
          />
          {errors.otp && <span className="block mb-2 text-sm text-red-600 dark:text-red-500">{errors.otp}</span>}
          <div className="flex flex-col space-y-2">
            <button
              className="px-4 py-2 text-black bg-white border border-black rounded-md"
              onClick={e => {
                console.log("Action pressed");
                updateEmailWithMF();
              }}
              disabled={!isValidForm}
            >
              Continue
            </button>
            <button
              className="px-4 py-2 text-red-600 border border-black rounded-md"
              onClick={() => {
                close();
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3 p-6 bg-white rounded-lg shadow-lg w-80">
          <h2 className="mb-4 text-lg font-semibold">Change Email</h2>
          <input
            name="email"
            id="email"
            type="text"
            value={email}
            placeholder="Email"
            onChange={e => {
              const val = e.target.value;
              setEmail(val);
            }}
            onFocus={onInputEvent}
            onBlur={onInputEvent}
            className="w-full h-[36px] rounded-md border border-gray-300 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {errors.email && <span className="block mb-2 text-sm text-red-600 dark:text-red-500">{errors.email}</span>}
          <input
            name="verifyEmail"
            id="verifyEmail"
            type="text"
            value={verifyEmail}
            placeholder="Verify Email"
            onChange={e => {
              const val = e.target.value;
              setVerifyEmail(val);
            }}
            onFocus={onInputEvent}
            onBlur={onInputEvent}
            className="w-full h-[36px] rounded-md border border-gray-300 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {errors.verifyEmail && <span className="block mb-2 text-sm text-red-600 dark:text-red-500">{errors.verifyEmail}</span>}
          <input
            name="password"
            type="password"
            value={password}
            placeholder="your password"
            onChange={e => {
              const val = e.target.value;
              setPassword(val);
            }}
            onFocus={onInputEvent}
            onBlur={onInputEvent}
            className="w-full h-[36px] rounded-md border border-gray-300 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {errors.password && <span className="block mb-2 text-sm text-red-600 dark:text-red-500">{errors.password}</span>}

          <div className="flex flex-col space-y-2">
            <button
              className="px-4 py-2 text-black bg-white border border-black rounded-md"
              onClick={e => {
                console.log("Action pressed");
                updateEmailAddress();
              }}
              disabled={!isValidForm}
            >
              Update
            </button>
            <button
              className="px-4 py-2 text-red-600 border border-black rounded-md"
              onClick={() => {
                close();
              }}
            >
              Cancel
            </button>
          </div>
          <div id="recaptcha-container"></div>
        </div>
      )}
    </div>
  );
};

export default EditEmail;
